<template>
	<div class="w-full">
		<div
			class="contained-section relative flex h-fit flex-col items-center overflow-clip bg-gray-100 bg-gradient-to-b from-[#00A1FF]/0 to-[#E3EEFA] sm:h-[420px] md:h-[440px] md:flex-col md:items-center md:gap-2 lg:h-[240px] lg:flex-row"
			:class="whitelabel.styles?.button.borderRadius"
		>
			<div class="flex w-full lg:w-1/3 xl:w-1/2">
				<div
					class="flex w-full flex-col gap-1 md:items-center lg:items-start"
				>
					<CarbarBadge class="w-fit" background-color="bg-green-600">
						<span
							class="text-xss font-semibold uppercase leading-none"
						>
							New
						</span>
					</CarbarBadge>
					<h2 class="mb-0 leading-none text-blue-400">
						Group subscription
					</h2>
					<p class="text-gray-700">Unlock better value, together</p>
					<CarbarImage
						image="img/partners/carsales/logo-carsales.webp"
						alt-text="carsales"
						image-width-class="w-[70px]"
						image-height-class="auto"
						class="relative -left-[8px] mt-2"
					/>
				</div>
			</div>

			<ClientOnly>
				<div
					v-if="!isLoading"
					class="flex w-full flex-col items-center sm:flex-row lg:w-2/3 xl:w-1/2"
				>
					<div
						class="order-last w-full sm:order-first sm:w-1/2 md:w-full"
					>
						<div
							class="flex h-full flex-col items-center sm:items-end"
						>
							<div class="text-sm font-medium text-gray-700">
								{{ featuredDeal?.year }}
							</div>
							<div
								class="my-1 font-heading text-xl font-semibold leading-none text-gray-700"
							>
								{{ featuredDeal?.name }}
							</div>
							<div class="text-sm font-medium text-gray-700">
								{{ featuredDeal?.variant }}
							</div>
							<div class="flex items-center gap-1">
								<span
									class="self-start text-lg font-semibold leading-none text-gray-600 line-through"
								>
									{{
										formatCurrency(
											featuredDeal?.original_weekly_price
										)
									}}
								</span>
								<span
									class="text-3xl leading-none text-blue-400"
									:class="
										partnerName
											? 'font-bold'
											: 'font-semibold'
									"
								>
									{{
										formatCurrency(
											featuredDeal?.unlock_weekly_price
										)
									}}
								</span>
								<span class="text-sm text-gray-700">
									per week
								</span>
							</div>
							<CarbarBadge
								class="mt-1"
								background-color="bg-green-600"
							>
								Savings of
								<span class="px-1 font-semibold">
									{{
										formatCurrency(
											featuredDeal?.weekly_savings
										)
									}}
								</span>
								per week
							</CarbarBadge>

							<DoubleCta
								class="mt-4 w-full md:w-auto"
								width="w-full"
								:is-stacked="false"
							>
								<template #primary>
									<CarbarLink
										:href="ctaGroupSubsDeals"
										class="w-full"
										:underline="false"
									>
										<CarbarButton
											:is-full-width="true"
											:size="3"
											:rounded="
												whitelabel.styles?.button
													.borderRadius
											"
											:background-color="
												partnerName
													? whitelabel.styles?.button
															.bg
													: 'bg-blue-400 hover:bg-sky-600'
											"
										>
											View deal
										</CarbarButton>
									</CarbarLink>
								</template>

								<template #secondary>
									<CarbarLink
										:href="ctaGroupSubs"
										class="w-full"
										:underline="false"
									>
										<CarbarButton
											:is-full-width="false"
											:is-outline="true"
											:is-mixed="false"
											class="group"
											:outline-color="
												whitelabel?.styles?.button
													?.outline
											"
											:text-color="
												whitelabel?.styles?.button
													?.outlineText
											"
											:border="
												whitelabel?.styles?.button
													?.borderRadius
											"
											:rounded="
												whitelabel.styles?.button
													?.borderRadius
											"
											:size="3"
										>
											Learn more
										</CarbarButton>
									</CarbarLink>
								</template>
							</DoubleCta>
						</div>
					</div>

					<div class="w-full sm:w-1/2 md:w-full">
						<div
							v-if="hasImage"
							class="aspect-h-2 aspect-w-3 overflow-hidden"
						>
							<CarbarImage
								:image="featuredDeal?.primary_image ?? ''"
								:alt-text="
									featuredDeal?.primary_image_alt_text ?? ''
								"
								:class="
									isImageLoading ? 'opacity-0' : 'opacity-100'
								"
								:preload="true"
								class="h-full w-full object-cover object-center mix-blend-darken transition-opacity duration-300"
								densities="x1 x2"
								loading="lazy"
								@load="onImageLoad"
							/>
							<PreloaderCircle :loading-state="isImageLoading" />
						</div>
						<CarbarImage
							v-else
							image="img/product-catalog/img-comingsoon.webp"
							alt-text="Coming soon"
							class="mix-blend-multiply"
							image-width-class="w-[300px]"
						/>
					</div>
				</div>
			</ClientOnly>

			<TermsAndConditions
				:is-full-width="false"
				custom-class="mt-4 sm:mt-0 sm:absolute sm:right-4 sm:bottom-4 z-50"
				font-color="text-gray-600"
			>
				<CarbarLink
					text="Terms & conditions"
					text-color="text-gray-600"
					underline-color="decoration-gray-600"
					hover-color="hover:decoration-gray-700 hover:text-gray-700"
					font-size="text-xsm"
					class="cursor-pointer"
					to="/terms-and-conditions/group-subscription"
				/>
				apply.
			</TermsAndConditions>
		</div>
	</div>
</template>

<script setup lang="ts">
import CarbarBadge from '@/components/badges/CarbarBadge.vue'
import CarbarButton from '@/components/buttons/CarbarButton.vue'
import DoubleCta from '@/components/buttons/DoubleCta.vue'
import CarbarImage from '@/components/CarbarImage.vue'
import TermsAndConditions from '@/components/fineprint/TermsAndConditions.vue'
import CarbarLink from '@/components/links/CarbarLink.vue'
import PreloaderCircle from '@/components/preloaders/PreloaderCircle.vue'
import { useGroupSubscription } from '@/stores/group-subscription'
import type { VehicleDealData } from '@/types/vehicle'

const { whitelabel } = useWhitelabelsStyles()
const { partnerName } = useGetPartnerName()

const route = useRoute()
const groupSubscriptionStore = useGroupSubscription()

const isImageLoading = ref(true)

const isLoading = computed(() => groupSubscriptionStore.getIsLoading)

const featuredDeal = computed<VehicleDealData | null>(
	() => groupSubscriptionStore.getIsFeatured
)

const hasImage = computed(() => featuredDeal.value?.primary_image)

const isHomepage = computed(() => route.path === '/')

const ctaGroupSubsDeals = computed(() => {
	const basePath = `/partners/carsales${featuredDeal.value?.cta_url}`
	return isHomepage.value ? `${basePath}?ref=homepage` : basePath
})

const ctaGroupSubs = computed(() => {
	const basePath = '/partners/carsales/group-subscription'
	return isHomepage.value ? `${basePath}?ref=homepage` : basePath
})

const onImageLoad = () => {
	isImageLoading.value = false
}

onMounted(async () => {
	try {
		await groupSubscriptionStore.fetchAllGroupBuyOffers()
	} catch (error) {
		console.error('Group subs failed:', error)
	}
})
</script>
